import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    ChevronRight,
    Package,
    CheckSquare,
    Edit2,
    Palette,
    Plus,
    Copy,
    ArrowRight,
    History,
    Pencil,
    Trash2,
    Calendar,
} from 'lucide-react';
import { Card, Badge, toast, ConfirmationModal, LoadingState, ErrorState } from '../../components/v2/V2Components';

import RoomService from '../../services/RoomService';
import PaintService from '../../services/PaintService';
import UserFloorPlanService from '../../services/UserFloorPlanService';
import RoomImageManager from '../../components/v2/RoomDetails/RoomImageManager';
import FloorPlanViewer from '../../components/v2/FloorPlanViewer';
import FloorPlanErrorBoundary from '../../components/v2/FloorPlanErrorBoundary';
import SceneObjectList from '../../components/v2/SceneObjectList';
import FloorPlanSourceBar from '../../components/v2/FloorPlanEditor/FloorPlanSourceBar';
import FloorPlanEmptyState from '../../components/v2/FloorPlanEditor/FloorPlanEmptyState';
import FloorPlanEditorPanel from '../../components/v2/FloorPlanEditor/FloorPlanEditorPanel';
import PaintFormModal from '../../components/v2/Paint/PaintFormModal';
import RoomPaintModal from '../../components/v2/Paint/RoomPaintModal';
import { UNIT_SYSTEMS } from '../../utils/measurementUtils';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';
import { FEATURES } from '../../featureFlags';


// Tabs a caller may pre-select via ?tab= (e.g. the floor-detail form returns
// to /room/:id?tab=floor after save). Unknown values fall back to 'paint'.
// 'tasks' is only valid when Maintenance is enabled — otherwise the tab is
// dropped from the strip, so ?tab=tasks must not resurrect the gated flow.
const initialTabFromSearch = (search) => {
    const validTabs = ['paint', 'floor', 'items', ...(FEATURES.maintenance ? ['tasks'] : [])];
    const requested = new URLSearchParams(search).get('tab');
    return validTabs.includes(requested) ? requested : 'paint';
};

const RoomDetails = () => {
    usePageView(ANALYTICS_EVENTS.SPACES_ROOM_DETAILS_VIEWED);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [room, setRoom] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(() => initialTabFromSearch(location.search));

    // Keep activeTab in sync with the URL when the room id or ?tab= query
    // changes WITHOUT a remount. React reuses this component across /room/:id
    // navigations (id changes) and query-only changes (/room/7?tab=paint →
    // ?tab=floor), so neither triggers a fresh useState. A render-phase
    // adjustment guarded by a prev-key — React's supported pattern for deriving
    // state from changing props — resyncs the tab with no stale-tab flash.
    // Tab-button clicks call setActiveTab without touching the URL, so the key
    // is unchanged and the user's manual selection is preserved.
    const tabSyncKey = `${id}::${location.search}`;
    const [prevTabSyncKey, setPrevTabSyncKey] = useState(tabSyncKey);
    if (tabSyncKey !== prevTabSyncKey) {
        setPrevTabSyncKey(tabSyncKey);
        setActiveTab(initialTabFromSearch(location.search));
    }
    const [itemsPagination, setItemsPagination] = useState({ offset: 0, hasMore: false, total: 0 });
    const [tasksPagination, setTasksPagination] = useState({ offset: 0, hasMore: false, total: 0 });
    const [loadingMore, setLoadingMore] = useState({ items: false, tasks: false });

    // Paint data — RoomPaint junction rows with embedded `paint` per row.
    // Two modal flavors: PaintFormModal edits the shared Paint identity
    // (changes ripple to every room that references it); RoomPaintModal
    // edits the per-room junction metadata (surface, date_painted, notes).
    const [roomPaints, setRoomPaints] = useState([]);
    const [paintLoading, setPaintLoading] = useState(false);
    const [showPaintFormModal, setShowPaintFormModal] = useState(false);
    const [editingSharedPaint, setEditingSharedPaint] = useState(null);
    const [paintSaving, setPaintSaving] = useState(false);
    const [showRoomPaintModal, setShowRoomPaintModal] = useState(false);
    const [editingRoomPaint, setEditingRoomPaint] = useState(null);
    const [roomPaintSaving, setRoomPaintSaving] = useState(false);

    // Floor data - loaded from API. Create/edit happens on a dedicated page
    // (FloorDetailForm) rather than a modal, so no open/editing/saving state here.
    const [floorDetails, setFloorDetails] = useState([]);
    const [floorLoading, setFloorLoading] = useState(false);

    // Floor plan state
    const [floorPlanSelectedId, setFloorPlanSelectedId] = useState(null);
    const [sceneData, setSceneData] = useState({
        objects: [], walls: [], doors: [], windows: [],
        // Additive: floors and openings used to live silently inside the
        // scan payload without ever reaching the sidebar. `unknown` is a
        // diagnostic bucket the backend fills when iOS sends a top-level
        // key the ingest doesn't recognize (e.g. a future RoomPlan
        // ``ceilings`` array). Empty by default — section only renders
        // when something arrives.
        floors: [], openings: [], unknown: [],
        // Backend-computed floor area (m²) + provenance + true polygon
        // footprint [w, d] in meters. Displayed, never recomputed (ADR 0006).
        // floorSections: per-room breakdown for multi-section scans.
        floorArea: null, floorAreaSource: null, floorFootprintM: null, floorSections: [],
        // Derived wall measurements keyed by wall UUID (net area + per-opening
        // gap/corner/sill). Display only; computed server-side.
        wallMeasurements: {},
    });
    // Per-object scene visibility. A Set of hidden scene identifiers shared
    // by FloorPlanViewer (which filters them out of the 3D render) and
    // SceneObjectList (which renders the per-row / per-section eye toggles).
    // Empty = everything visible. Session-only — resets on reload, like the
    // old "Hide Walls" toggle it replaces.
    const [hiddenIds, setHiddenIds] = useState(() => new Set());
    const toggleItemVisibility = useCallback((id) => {
        setHiddenIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);
    const toggleSectionVisibility = useCallback((ids, makeVisible) => {
        setHiddenIds(prev => {
            const next = new Set(prev);
            ids.forEach(id => {
                if (makeVisible) next.delete(id);
                else next.add(id);
            });
            return next;
        });
    }, []);
    const [floorPlanUnitSystem, setFloorPlanUnitSystem] = useState(UNIT_SYSTEMS.INCHES);
    const [displayMode, setDisplayMode] = useState('images'); // 'images' or 'floorplan'

    // User-created floor plans. activePlanId null = show the scanned room
    // (the viewer's own roomId fetch). When a plan is active its full
    // response feeds the viewer via previewData; while the editor is open
    // the live editor-derived preview takes priority so the 3D scene
    // tracks every keystroke.
    const [userPlans, setUserPlans] = useState([]);
    const [activePlanId, setActivePlanId] = useState(null);
    const [activePlanData, setActivePlanData] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorPreview, setEditorPreview] = useState(null);
    const [planActionBusy, setPlanActionBusy] = useState(false);
    // Outcome of the scanned-room fetch, reported up by FloorPlanViewer:
    // 'unknown' | 'loading' | 'present' | 'empty' | 'error'. Drives the
    // no-scan empty-state guide and gates the "From Scan" action.
    const [scanStatus, setScanStatus] = useState('unknown');
    const [planToDelete, setPlanToDelete] = useState(null);
    const [deletingPlan, setDeletingPlan] = useState(false);

    // Delete Modal State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingRoom, setDeletingRoom] = useState(false);
    // Detach removes the junction row; the underlying Paint persists in the
    // library so the user can re-attach it elsewhere. To delete a Paint
    // entirely the user goes to the Paint Library page.
    const [roomPaintToDetach, setRoomPaintToDetach] = useState(null);
    const [detachingRoomPaint, setDetachingRoomPaint] = useState(false);
    const [floorToDelete, setFloorToDelete] = useState(null);
    const [deletingFloor, setDeletingFloor] = useState(false);

    // Track loaded tabs to prevent duplicate fetches
    const loadedTabs = useRef(new Set());
    // Monotonic guards for floor-plan async work. RoomDetails is reused
    // (not remounted) across /room/:id navigations, so an in-flight
    // getPlan / getPlansForRoom from the previous room can resolve AFTER the
    // room-change reset and clobber the cleared plan state — rendering the
    // old room's geometry into the new room.
    //
    // Two refs, not one: planRequestRef guards plan selection/creation/
    // conversion; planListRequestRef guards the plan-LIST fetch. Sharing one
    // ref made every selection/mutation silently drop an in-flight list
    // response, leaving pre-existing plans missing from the source bar.
    // Resolvers ignore their result when superseded.
    const planRequestRef = useRef(0);
    const planListRequestRef = useRef(0);
    // Same guard for the room-paint and floor-detail lists: a fetch from the
    // previous room must not repopulate the new room's list after the
    // room-change reset. currentRoomIdRef covers the other half: save/detach/
    // delete handlers hold the old room's fetch in their closures, so one that
    // finishes after a room change would otherwise START a fresh old-room
    // fetch, which takes the latest request id and wins.
    const paintRequestRef = useRef(0);
    const floorRequestRef = useRef(0);
    const currentRoomIdRef = useRef(id);
    // True while the user is in the "create a paint from inside the attach
    // modal" flow. The PaintFormModal save handler reads this to know whether
    // to immediately re-open the RoomPaintModal with the freshly-created paint
    // pre-selected, so the user doesn't have to re-navigate to attach it.
    const pendingAttachAfterCreate = useRef(false);

    const fetchRoomDetails = useCallback(async () => {
        try {
            const data = await RoomService.getRoom(id);
            setRoom({
                ...data,
                items: data.items || [],
                tasks: data.tasks || []
            });
            setError(null);
        } catch (err) {
            if (err.code === 404) {
                setError('Room not found. It may have been deleted.');
            } else if (err.code === 401) {
                setError('Authentication required. Please log in again.');
            } else {
                setError(err.message || 'Failed to load room details.');
            }
        }
    }, [id]);

    const fetchItems = useCallback(async (offset = 0, append = false) => {
        try {
            const data = await RoomService.getRoomItems(id, { limit: 50, offset });
            if (!data || !data.items || !data.pagination) return;
            setRoom(prev => ({
                ...prev,
                items: append ? [...(prev.items || []), ...data.items] : data.items
            }));
            setItemsPagination({
                offset: data.pagination.offset + data.pagination.limit,
                hasMore: data.pagination.has_more,
                total: data.pagination.total
            });
        } catch (err) {
            console.error("Failed to load items:", err);
        }
    }, [id]);

    const fetchTasks = useCallback(async (offset = 0, append = false) => {
        try {
            const data = await RoomService.getRoomTasks(id, { limit: 50, offset });
            if (!data || !data.tasks || !data.pagination) return;
            setRoom(prev => ({
                ...prev,
                tasks: append ? [...(prev.tasks || []), ...data.tasks] : data.tasks
            }));
            setTasksPagination({
                offset: data.pagination.offset + data.pagination.limit,
                hasMore: data.pagination.has_more,
                total: data.pagination.total
            });
        } catch (err) {
            console.error("Failed to load tasks:", err);
        }
    }, [id]);

    const fetchRoomPaints = useCallback(async () => {
        if (currentRoomIdRef.current !== id) return; // stale closure from a previous room
        paintRequestRef.current += 1;
        const requestId = paintRequestRef.current;
        setPaintLoading(true);
        try {
            const data = await PaintService.getRoomPaints(id);
            if (paintRequestRef.current !== requestId) return; // superseded
            setRoomPaints(data);
        } catch (err) {
            if (paintRequestRef.current !== requestId) return; // superseded
            console.error('Failed to load room paints:', err);
        } finally {
            if (paintRequestRef.current === requestId) setPaintLoading(false);
        }
    }, [id]);

    const fetchFloorDetails = useCallback(async () => {
        if (currentRoomIdRef.current !== id) return; // stale closure from a previous room
        floorRequestRef.current += 1;
        const requestId = floorRequestRef.current;
        setFloorLoading(true);
        try {
            const data = await RoomService.getRoomFloorDetails(id);
            if (floorRequestRef.current !== requestId) return; // superseded
            setFloorDetails(data);
        } catch (err) {
            if (floorRequestRef.current !== requestId) return; // superseded
            console.error('Failed to load floor details:', err);
        } finally {
            if (floorRequestRef.current === requestId) setFloorLoading(false);
        }
    }, [id]);

    const fetchUserPlans = useCallback(async () => {
        // Each list fetch claims a new generation, so the newest call always
        // wins regardless of network ordering (same pattern as the viewer's
        // loadGenerationRef). Room changes also bump this to drop the old
        // room's in-flight response.
        const requestId = ++planListRequestRef.current;
        try {
            const data = await UserFloorPlanService.getPlansForRoom(id);
            if (planListRequestRef.current !== requestId) return; // superseded
            setUserPlans(data.plans || []);
            // Mark the lazy-load cache ONLY on success — flagging before the
            // fetch resolves left the room stuck with an empty plan list on a
            // transient failure (re-entering floor-plan mode never retried).
            loadedTabs.current.add('floorplans');
        } catch (err) {
            console.error('Failed to load user floor plans:', err);
        }
    }, [id]);

    useEffect(() => {
        loadedTabs.current.clear();
        // Per-room state reset when the room changes. This component is reused
        // (not remounted) across /room/:id navigations. The active tab is
        // synced from the URL during render (see tabSyncKey above), so it is
        // intentionally not set here.
        // Plan state is per-room — clear it so a stale plan from the
        // previous room can never render into the new one. Bumping the
        // request guards invalidates any in-flight plan/list fetch from the
        // old room so its late resolution can't repopulate this cleared
        // state.
        planRequestRef.current += 1;
        planListRequestRef.current += 1;
        setUserPlans([]);
        setActivePlanId(null);
        setActivePlanData(null);
        setScanStatus('unknown');
        setEditorOpen(false);
        setEditorPreview(null);
        // Modal / busy / selection state is also per-room: a delete-confirm
        // modal or busy spinner left open during navigation would act on the
        // OLD room's plan, and selection/visibility ids don't exist in the
        // new room's scene.
        setPlanToDelete(null);
        setDeletingPlan(false);
        setPlanActionBusy(false);
        setFloorPlanSelectedId(null);
        setHiddenIds(new Set());
        // Same for the paint junction editors: an edit or detach left open
        // would act on the OLD room's row id against the new room's id, and a
        // pending attach-after-create belongs to the room it started in. The
        // shared-paint editor is library-wide, so it is left alone. The list
        // itself is cleared too, and its in-flight fetch invalidated, so the
        // old room's rows (and their row ids) never render under the new room.
        currentRoomIdRef.current = id;
        paintRequestRef.current += 1;
        setRoomPaints([]);
        setPaintLoading(false);
        setShowRoomPaintModal(false);
        setEditingRoomPaint(null);
        setRoomPaintToDetach(null);
        pendingAttachAfterCreate.current = false;
        // Floor details get the same treatment: clear the list, invalidate its
        // in-flight fetch, and close a delete confirm aimed at the old room.
        floorRequestRef.current += 1;
        setFloorDetails([]);
        setFloorLoading(false);
        setFloorToDelete(null);
        const roomId = parseInt(id);
        if (isNaN(roomId) || roomId <= 0) {
            setError('Invalid room ID');
            setLoading(false);
            return;
        }
        const loadInitialData = async () => {
            setLoading(true);
            await fetchRoomDetails();
            setLoading(false);
        };
        loadInitialData();
    }, [id, fetchRoomDetails]);

    useEffect(() => {
        if (!room) return;
        if (activeTab === 'items' && !loadedTabs.current.has('items') && room.items_count > 0) {
            loadedTabs.current.add('items');
            fetchItems(0, false);
        } else if (activeTab === 'tasks' && !loadedTabs.current.has('tasks') && room.tasks_count > 0) {
            loadedTabs.current.add('tasks');
            fetchTasks(0, false);
        } else if (activeTab === 'paint' && !loadedTabs.current.has('paint')) {
            loadedTabs.current.add('paint');
            fetchRoomPaints();
        } else if (activeTab === 'floor' && !loadedTabs.current.has('floor')) {
            loadedTabs.current.add('floor');
            fetchFloorDetails();
        }
        // Effect intentionally watches specific counts on `room`, not the
        // whole object — including `room` would re-run on any field change
        // (e.g. user edits a room name) and lose the loadedTabs cache.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, room?.items_count, room?.tasks_count, fetchItems, fetchTasks, fetchRoomPaints, fetchFloorDetails]);

    // Lazy-load the plan list the first time floor-plan mode opens for this
    // room (same loadedTabs pattern as the data tabs). fetchUserPlans sets
    // the 'floorplans' flag itself on SUCCESS, so a transient failure leaves
    // the flag unset and re-entering floor-plan mode retries; duplicate
    // in-flight fetches are harmless (newest generation wins).
    useEffect(() => {
        if (displayMode === 'floorplan' && room && !loadedTabs.current.has('floorplans')) {
            fetchUserPlans();
        }
    }, [displayMode, room, fetchUserPlans]);

    // ------------------------------------------------------------------
    // User floor plan handlers
    // ------------------------------------------------------------------
    // Selection + per-object visibility are keyed by surface identifiers,
    // which differ between the scan and each plan — clear them on switch.
    const resetSceneInteraction = useCallback(() => {
        setFloorPlanSelectedId(null);
        setHiddenIds(new Set());
    }, []);

    const handleSelectScan = useCallback(() => {
        // Already showing the scanned room — no-op. Resetting scanStatus here
        // would strand it at 'unknown' (the viewer keeps identical props, so it
        // never remounts/re-reports), silently hiding "From Scan".
        if (activePlanId === null) return;
        setActivePlanId(null);
        setActivePlanData(null);
        // Switching FROM a plan: previewData goes null, so the viewer re-fetches
        // and re-reports. Reset to 'unknown' so a stale/sticky status can't keep
        // the guide up (short-circuiting the viewer) before that report lands.
        setScanStatus('unknown');
        resetSceneInteraction();
    }, [activePlanId, resetSceneInteraction]);

    const handleSelectPlan = useCallback(async (planId) => {
        // Bump the guard so a slower earlier selection (or a room change)
        // can't overwrite this one when it resolves out of order.
        planRequestRef.current += 1;
        const requestId = planRequestRef.current;
        try {
            const data = await UserFloorPlanService.getPlan(planId);
            if (planRequestRef.current !== requestId) return; // superseded
            setActivePlanId(planId);
            setActivePlanData(data);
            resetSceneInteraction();
        } catch (err) {
            if (planRequestRef.current !== requestId) return; // superseded
            toast.error(err.message || 'Failed to load floor plan');
        }
    }, [resetSceneInteraction]);

    const handleNewPlan = useCallback(async () => {
        planRequestRef.current += 1;
        const requestId = planRequestRef.current;
        setPlanActionBusy(true);
        try {
            const created = await UserFloorPlanService.createPlan({
                room_id: parseInt(id),
                name: `Floor Plan ${userPlans.length + 1}`,
                label: room?.name || null,
            });
            if (planRequestRef.current !== requestId) return; // superseded
            setUserPlans((prev) => [created, ...prev]);
            setActivePlanId(created.id);
            setActivePlanData(created);
            resetSceneInteraction();
            setEditorOpen(true);
            // Optimistic prepend above for immediacy; this refetch claims a
            // newer list generation, so a slower pre-create list response
            // can't clobber the prepend, and the bar converges on the
            // authoritative server list (which now includes the new plan).
            fetchUserPlans();
        } catch (err) {
            toast.error(err.message || 'Failed to create floor plan');
        } finally {
            setPlanActionBusy(false);
        }
    }, [id, room?.name, userPlans.length, resetSceneInteraction, fetchUserPlans]);

    const handleConvertScan = useCallback(async () => {
        planRequestRef.current += 1;
        const requestId = planRequestRef.current;
        setPlanActionBusy(true);
        try {
            const created = await UserFloorPlanService.createFromScan({ room_id: parseInt(id) });
            if (planRequestRef.current !== requestId) return; // superseded
            setUserPlans((prev) => [created, ...prev]);
            setActivePlanId(created.id);
            setActivePlanData(created);
            resetSceneInteraction();
            toast.success('Scan converted to an editable plan');
            // Same convergence refetch as handleNewPlan — see comment there.
            fetchUserPlans();
        } catch (err) {
            toast.error(err.code === 404
                ? 'No room scan to convert — scan the room first.'
                : (err.message || 'Failed to convert scan'));
        } finally {
            setPlanActionBusy(false);
        }
    }, [id, resetSceneInteraction, fetchUserPlans]);

    const handleEditorSaved = useCallback((updated) => {
        setActivePlanData(updated);
        setUserPlans((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
        setEditorOpen(false);
        setEditorPreview(null);
        resetSceneInteraction();
    }, [resetSceneInteraction]);

    const handleEditorCancel = useCallback(() => {
        setEditorOpen(false);
        setEditorPreview(null);
        // Parity with handleEditorSaved: a wall selected for anchoring must
        // not stay highlighted in the post-cancel scanned view.
        resetSceneInteraction();
    }, [resetSceneInteraction]);

    const handleDeletePlan = useCallback(async () => {
        if (!planToDelete) return;
        setDeletingPlan(true);
        try {
            await UserFloorPlanService.deletePlan(planToDelete.id);
            setUserPlans((prev) => prev.filter((p) => p.id !== planToDelete.id));
            if (activePlanId === planToDelete.id) handleSelectScan();
            toast.success('Floor plan deleted');
        } catch (err) {
            toast.error(err.message || 'Failed to delete floor plan');
        } finally {
            setDeletingPlan(false);
            setPlanToDelete(null);
        }
    }, [planToDelete, activePlanId, handleSelectScan]);

    const loadMoreItems = async () => {
        setLoadingMore(prev => ({ ...prev, items: true }));
        await fetchItems(itemsPagination.offset, true);
        setLoadingMore(prev => ({ ...prev, items: false }));
    };

    const loadMoreTasks = async () => {
        setLoadingMore(prev => ({ ...prev, tasks: true }));
        await fetchTasks(tasksPagination.offset, true);
        setLoadingMore(prev => ({ ...prev, tasks: false }));
    };

    const goToCreateTask = () => {
        // Drop the cached "tasks" tab marker so it refetches when we come back
        // — otherwise the freshly-saved task is invisible until manual refresh.
        loadedTabs.current.delete('tasks');
        navigate('/maintenance/new', {
            state: { room_id: parseInt(id), returnTo: location.pathname },
        });
    };

    const copyHexCode = async (code) => {
        try {
            await navigator.clipboard.writeText(code);
            toast.success('Hex code copied');
        } catch {
            toast.error('Failed to copy hex code');
        }
    };

    // Create or update the shared Paint (the library entity). Changes propagate
    // to every room that references this paint via RoomPaint.
    const handleSaveSharedPaint = async (paintData) => {
        setPaintSaving(true);
        try {
            if (editingSharedPaint) {
                await PaintService.updatePaint(editingSharedPaint.id, paintData);
                toast.success('Paint updated');
            } else {
                // Create flow: the new paint is created in the library only.
                // Attaching it to this room happens through the RoomPaintModal
                // step that follows — see the pendingAttachAfterCreate ref and
                // the onCreatePaint handler passed to RoomPaintModal below.
                const created = await PaintService.createPaint(paintData);
                toast.success('Paint added to library');
                // After creating a brand-new paint via "Create new paint" inside
                // the RoomPaintModal flow, immediately attach it to the room
                // with a placeholder surface so the user lands in a useful state
                // instead of an empty library page.
                if (pendingAttachAfterCreate.current) {
                    pendingAttachAfterCreate.current = false;
                    setEditingRoomPaint({ paint_id: created.id, paint: created });
                    setShowRoomPaintModal(true);
                }
            }
            setShowPaintFormModal(false);
            setEditingSharedPaint(null);
            fetchRoomPaints();
        } catch (err) {
            toast.error(err.message || 'Failed to save paint');
        } finally {
            setPaintSaving(false);
        }
    };

    // Create or update the room↔paint junction row (per-application metadata).
    // Does NOT modify the underlying Paint.
    const handleSaveRoomPaint = async (junctionData) => {
        setRoomPaintSaving(true);
        try {
            if (editingRoomPaint && editingRoomPaint.id) {
                await PaintService.updateRoomPaint(id, editingRoomPaint.id, junctionData);
                toast.success('Updated how paint is applied');
            } else {
                await PaintService.attachPaintToRoom(id, junctionData);
                toast.success('Paint attached to room');
            }
            setShowRoomPaintModal(false);
            setEditingRoomPaint(null);
            fetchRoomPaints();
        } catch (err) {
            // Surface the 409 collision case clearly so the user knows what to fix.
            const msg = err.code === 409
                ? (err.message || 'This paint is already attached to that surface in this room.')
                : (err.message || 'Failed to save');
            toast.error(msg);
        } finally {
            setRoomPaintSaving(false);
        }
    };

    // Detach a paint from this room. The underlying Paint persists in the
    // library — to delete the Paint entirely the user goes to the library page.
    const handleDetachRoomPaint = async () => {
        if (!roomPaintToDetach) return;
        setDetachingRoomPaint(true);
        try {
            await PaintService.detachPaintFromRoom(id, roomPaintToDetach);
            toast.success('Paint detached from room');
            fetchRoomPaints();
            setRoomPaintToDetach(null);
        } catch (err) {
            toast.error(err.message || 'Failed to detach paint');
        } finally {
            setDetachingRoomPaint(false);
        }
    };

    const handleDeleteFloorDetail = async () => {
        if (!floorToDelete) return;
        setDeletingFloor(true);
        try {
            await RoomService.deleteRoomFloorDetail(id, floorToDelete);
            toast.success('Floor detail deleted');
            fetchFloorDetails();
            setFloorToDelete(null);
        } catch (err) {
            toast.error(err.message || 'Failed to delete floor detail');
        } finally {
            setDeletingFloor(false);
        }
    };

    const tabs = [
        { id: 'paint', label: 'Paint' },
        { id: 'floor', label: 'Floor' },
        { id: 'items', label: `Items (${itemsPagination.total || room?.items_count || 0})` },
        // Tasks is part of the Maintenance feature. While that feature is gated
        // off the tab is dropped entirely — its New Task / Create Task buttons
        // navigate to /maintenance/* (a "Coming Soon" placeholder when off), so
        // surfacing the tab would only lead users to a dead end.
        ...(FEATURES.maintenance
            ? [{ id: 'tasks', label: `Tasks (${tasksPagination.total || room?.tasks_count || 0})` }]
            : []),
    ];

    const handleDeleteRoom = async () => {
        setDeletingRoom(true);
        try {
            await RoomService.deleteRoom(id);
            toast.success('Room deleted successfully');
            navigate('/spaces');
        } catch (err) {
            toast.error(err.message || 'Failed to delete room');
            setDeletingRoom(false);
            setShowDeleteModal(false);
        }
    };

    if (loading) {
        return <LoadingState />;
    }

    if (error || !room) {
        const isNotFound = (error || '').startsWith('Room not found');
        return (
            <ErrorState
                title="Error Loading Room"
                message={error || 'Room not found'}
                onRetry={isNotFound
                    ? undefined
                    : () => { setLoading(true); setError(null); fetchRoomDetails().finally(() => setLoading(false)); }}
                action={{ label: 'Back to Rooms', onClick: () => navigate('/spaces') }}
            />
        );
    }

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Breadcrumb + Header */}
            <header>
                <nav className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mb-2 font-medium">
                    <button
                        onClick={() => navigate('/spaces')}
                        className="hover:text-[var(--primary)] transition-colors"
                    >
                        {room.floor || 'Rooms'}
                    </button>
                    <ChevronRight size={12} />
                    <button
                        onClick={() => navigate('/spaces')}
                        data-testid="back-to-spaces"
                        className="hover:text-[var(--primary)] transition-colors"
                    >
                        Spaces
                    </button>
                    <ChevronRight size={12} />
                    <span className="text-[var(--primary)] font-semibold">{room.name}</span>
                </nav>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <h1 className="font-headline text-3xl md:text-4xl font-extrabold text-[var(--text-main)] tracking-tight min-w-0 break-words">
                        {room.name}
                    </h1>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                        <button
                            onClick={() => navigate(`/spaces/edit/${id}`)}
                            className="flex items-center gap-2 px-4 py-2.5 bg-[var(--bg-hover)] text-[var(--text-main)] rounded-xl hover:bg-[var(--bg-elevated)] transition text-sm font-medium"
                        >
                            <Edit2 size={16} />
                            <span>Edit</span>
                        </button>
                        <button
                            onClick={() => setShowDeleteModal(true)}
                            className="flex items-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition text-sm font-medium dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40"
                        >
                            <Trash2 size={16} />
                            <span>Delete</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* View Toggle — lifted out of the canvas (was an absolute overlay
                inside the visual area) into normal page flow above both the
                carousel and the 3D viewer. This gives it permanent, dedicated
                space and structurally prevents it from overlapping
                FloorPlanViewer's own top-left controls (3D/2D, Walls,
                Measurements, units). */}
            <div className="flex justify-center">
                <div className="bg-[var(--bg-hover)] p-1 rounded-full flex gap-1 shadow-sm">
                    <button
                        onClick={() => setDisplayMode('images')}
                        className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${displayMode === 'images'
                            ? 'bg-[var(--primary)] text-[var(--primary-text)]'
                            : 'text-[var(--text-muted)] hover:text-[var(--primary)]'
                        }`}
                        aria-label="Switch to image carousel view"
                    >
                        Images
                    </button>
                    <button
                        onClick={() => {
                            // Only reset on a real images→floorplan transition.
                            // The viewer re-probes when it (re)mounts; a redundant
                            // click while already in floor-plan mode keeps the
                            // same props (no remount), so resetting here would
                            // strand scanStatus at 'unknown' and hide "From Scan".
                            const enteringFloorPlan = displayMode !== 'floorplan';
                            setDisplayMode('floorplan');
                            // Re-probe on entry: a sticky 'empty' would otherwise
                            // short-circuit to the guide and prevent the viewer
                            // from remounting to re-check the scan (e.g. after the
                            // user scans the room via the app).
                            if (enteringFloorPlan && activePlanId === null) setScanStatus('unknown');
                        }}
                        className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${displayMode === 'floorplan'
                            ? 'bg-[var(--primary)] text-[var(--primary-text)]'
                            : 'text-[var(--text-muted)] hover:text-[var(--primary)]'
                        }`}
                        aria-label="Switch to floor plan view"
                    >
                        Floor Plan
                    </button>
                </div>
            </div>

            {/* Floor-plan source selector: the immutable scanned room vs.
                user-created (editable) plans, plus create/convert actions.
                Only meaningful in floor-plan mode. */}
            {displayMode === 'floorplan' && (
                <div className="flex justify-center">
                    <FloorPlanSourceBar
                        plans={userPlans}
                        activePlanId={activePlanId}
                        editorOpen={editorOpen}
                        busy={planActionBusy}
                        hasScan={scanStatus === 'present'}
                        onSelectScan={handleSelectScan}
                        onSelectPlan={handleSelectPlan}
                        onNewPlan={handleNewPlan}
                        onConvertScan={handleConvertScan}
                        onEdit={() => setEditorOpen(true)}
                        onDelete={() => setPlanToDelete(userPlans.find((p) => p.id === activePlanId) || null)}
                    />
                </div>
            )}

            {/* Visual Interaction Area — Split Layout */}
            {/* Height: target 80vh on md+, with the section stretching both columns at lg.
                On md the canvas takes 80vh and the sidebar flows below.
                The Scene Objects sidebar only renders in Floor Plan mode — its
                content is scan data that has no meaning for the image carousel.
                In Images mode the canvas spans the full width. */}
            <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:h-[80vh] lg:min-h-[480px] lg:max-h-screen">
                {/* Main Visual (Floor Plan / Image) — full width in Images mode,
                    8/12 when the Scene Objects sidebar is present. */}
                <div className={`${displayMode === 'floorplan' ? 'lg:col-span-8' : 'lg:col-span-12'} lg:min-h-0 h-[80vh] lg:h-auto max-h-screen bg-[var(--bg-hover)] rounded-[2rem] overflow-hidden relative`}>
                    {displayMode === 'floorplan' ? (
                        <div className="h-full min-h-[400px]">
                            {/* Scanned-room mode with no scan → friendly guide
                                instead of the viewer's error card. The viewer
                                still mounts first (scanStatus 'unknown'), reports
                                'empty' from its roomId fetch, and this swaps in.
                                Genuine (non-404) failures report 'error' and keep
                                the viewer's own error+retry card. */}
                            {activePlanId === null && scanStatus === 'empty' ? (
                                <FloorPlanEmptyState
                                    onCreatePlan={handleNewPlan}
                                    busy={planActionBusy}
                                />
                            ) : (
                                <FloorPlanErrorBoundary>
                                    <FloorPlanViewer
                                        roomId={room.id}
                                        // null when the scanned room is active →
                                        // the viewer fetches by roomId as before.
                                        // While editing, the live editor preview
                                        // wins (falling back to the saved plan
                                        // for the first paint).
                                        previewData={editorOpen ? (editorPreview ?? activePlanData) : activePlanData}
                                        className="h-full"
                                        selectedId={floorPlanSelectedId}
                                        onSelect={setFloorPlanSelectedId}
                                        onSceneDataLoad={setSceneData}
                                        hiddenIds={hiddenIds}
                                        unitSystem={floorPlanUnitSystem}
                                        onUnitSystemChange={setFloorPlanUnitSystem}
                                        onScanStatusChange={setScanStatus}
                                    />
                                </FloorPlanErrorBoundary>
                            )}
                        </div>
                    ) : (
                        <RoomImageManager
                            roomId={room.id}
                            images={room.images || []}
                            onChange={fetchRoomDetails}
                        />
                    )}
                </div>

                {/* Scene Objects Sidebar — Floor Plan mode only. The list is
                    scan data (walls/doors/windows/objects) pushed up from
                    FloorPlanViewer; it has no meaning while the image carousel
                    is showing, so the whole sidebar is gated on displayMode.
                    This also makes the stale-data leak structurally impossible
                    — sceneData can never drive UI outside floor-plan mode. */}
                {displayMode === 'floorplan' && (
                    <div className="lg:col-span-4 lg:min-h-0 flex flex-col gap-5">
                        {/* No wrapper heading here — SceneObjectList renders its
                            own per-section stat headers (Objects / Walls / Doors
                            / Windows), which are the single source of truth for
                            the scene-object counts. A second "Scene Objects"
                            <h3> here would just stack a duplicate. */}
                        {/* No visibility guard here — SceneObjectList renders
                            its own "No objects in scene" empty state, and it
                            also lists walls/doors/windows. A guard on
                            `objects.length` alone would wrongly hide a scan
                            that captured only architectural elements. */}
                        <div className="flex-1 overflow-y-auto no-scrollbar">
                            {editorOpen && activePlanData ? (
                                <FloorPlanEditorPanel
                                    key={activePlanData.id}
                                    plan={activePlanData}
                                    unitSystem={floorPlanUnitSystem}
                                    onPreviewChange={setEditorPreview}
                                    onSaved={handleEditorSaved}
                                    onCancel={handleEditorCancel}
                                    // Viewer clicks land here even while editing —
                                    // the anchor card reads the selected scanned
                                    // wall and clears the pick after use.
                                    selectedId={floorPlanSelectedId}
                                    onSelect={setFloorPlanSelectedId}
                                />
                            ) : (
                            <SceneObjectList
                                objects={sceneData.objects}
                                walls={sceneData.walls}
                                doors={sceneData.doors}
                                windows={sceneData.windows}
                                floors={sceneData.floors}
                                openings={sceneData.openings}
                                unknown={sceneData.unknown}
                                floorArea={sceneData.floorArea}
                                floorAreaSource={sceneData.floorAreaSource}
                                floorFootprintM={sceneData.floorFootprintM}
                                floorSections={sceneData.floorSections}
                                wallMeasurements={sceneData.wallMeasurements}
                                selectedId={floorPlanSelectedId}
                                onSelect={setFloorPlanSelectedId}
                                hiddenIds={hiddenIds}
                                onToggleItem={toggleItemVisibility}
                                onToggleSection={toggleSectionVisibility}
                                unitSystem={floorPlanUnitSystem}
                                className="h-full"
                            />
                            )}
                        </div>

                        {/* Service History lives on the gated Maintenance page;
                            hide the entry point while the feature is off. */}
                        {FEATURES.maintenance && (
                            <button
                                onClick={() => navigate(`/maintenance`)}
                                className="w-full py-3.5 bg-[var(--primary)] text-white rounded-2xl font-bold flex items-center justify-center gap-2 shadow-[0_12px_32px_rgba(3,37,33,0.1)] active:scale-95 transition-all text-sm"
                            >
                                <History size={16} />
                                View Service History
                            </button>
                        )}
                    </div>
                )}
            </section>

            {/* Tab Navigation for secondary content */}
            <div className="flex gap-1 bg-[var(--bg-hover)] p-1 rounded-xl overflow-x-auto">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${activeTab === tab.id
                            ? 'bg-[var(--bg-card)] text-[var(--text-main)] shadow-sm font-semibold'
                            : 'text-[var(--text-secondary)] hover:text-[var(--text-main)]'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div className="min-h-[200px]">
                {/* Paint Tab */}
                {activeTab === 'paint' && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <p className="text-[var(--text-secondary)] text-sm theme-transition">
                                Track paint colors and finishes for easy touch-ups and repainting
                            </p>
                        </div>

                        {paintLoading ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {roomPaints.map((rp) => {
                                    // The embedded Paint carries identity (color, manufacturer,
                                    // sheen, codes). The junction row (rp) carries per-room
                                    // application metadata (surface, date_painted, notes).
                                    const paint = rp.paint || {};
                                    // The Paint API switched from a single image_url to
                                    // a multi-image gallery (paint.images[]). Derive the
                                    // primary swatch from the array — fall back to the
                                    // first image if nothing is flagged primary, which
                                    // can happen briefly during the auto-promote race.
                                    const primaryImage = paint.images?.find(i => i.is_primary);
                                    const imageUrl = primaryImage?.url || paint.images?.[0]?.url || null;
                                    return (
                                        <Card key={rp.id} data-testid={`room-paint-card-${rp.id}`} className="overflow-hidden">
                                            {/* Label image (R2 presigned) or neutral swatch.
                                                Phase 0 confirmed hex codes are never on real
                                                labels, so the swatch is intentionally a neutral
                                                grey block + the color name as label. */}
                                            {imageUrl ? (
                                                <div className="h-40 w-full relative">
                                                    <img
                                                        src={imageUrl}
                                                        alt={`${paint.color_name || 'Paint'} label`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="h-24 w-full bg-[var(--bg-hover)] flex items-center justify-center theme-transition">
                                                    <span className="text-sm text-[var(--text-muted)] uppercase tracking-wide">
                                                        {paint.color_name || 'Unnamed paint'}
                                                    </span>
                                                </div>
                                            )}

                                            <div className="p-5">
                                                <div className="flex justify-between items-start mb-3">
                                                    <div>
                                                        {/* Surface (the per-room application identity) is the H3 — this card is
                                                            "the paint on the Walls" not "the paint named Manual Opaline". */}
                                                        <h3 className="font-headline font-bold text-lg text-[var(--text-main)] theme-transition">
                                                            {rp.surface}
                                                        </h3>
                                                        {paint.color_name && (
                                                            <p className="text-sm text-[var(--text-secondary)] theme-transition">
                                                                {paint.color_name}
                                                                {paint.manufacturer && ` — ${paint.manufacturer}`}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="flex gap-1">
                                                        {/* Two pencils: one for shared paint identity,
                                                            one for room application metadata. Read-side
                                                            disambiguation per the plan — no warning
                                                            dialogs, the choice is structural. */}
                                                        <button
                                                            onClick={() => { setEditingSharedPaint(paint); setShowPaintFormModal(true); }}
                                                            className="p-1.5 rounded-md hover:bg-[var(--bg-hover)] text-[var(--text-muted)] hover:text-[var(--primary)] transition theme-transition"
                                                            title="Edit shared paint (affects all rooms using this paint)"
                                                        >
                                                            <Palette size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => { setEditingRoomPaint(rp); setShowRoomPaintModal(true); }}
                                                            className="p-1.5 rounded-md hover:bg-[var(--bg-hover)] text-[var(--text-muted)] hover:text-[var(--primary)] transition theme-transition"
                                                            title="Edit how it's applied here (surface, date)"
                                                        >
                                                            <Pencil size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => setRoomPaintToDetach(rp.id)}
                                                            className="p-1.5 rounded-md hover:bg-red-50 text-[var(--text-muted)] hover:text-red-500 transition theme-transition"
                                                            title="Detach from this room (keeps the paint in your library)"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="space-y-2 text-sm">
                                                    {paint.color_code && (
                                                        <div className="flex justify-between py-1 border-b border-[var(--border)] theme-transition">
                                                            <span className="text-[var(--text-secondary)] theme-transition">Code</span>
                                                            <span className="font-medium text-[var(--text-main)] font-mono theme-transition">{paint.color_code}</span>
                                                        </div>
                                                    )}
                                                    {paint.sheen && (
                                                        <div className="flex justify-between py-1 border-b border-[var(--border)] theme-transition">
                                                            <span className="text-[var(--text-secondary)] theme-transition">Sheen</span>
                                                            <span className="font-medium text-[var(--text-main)] theme-transition">{paint.sheen}</span>
                                                        </div>
                                                    )}
                                                    {paint.product_line && (
                                                        <div className="flex justify-between py-1 border-b border-[var(--border)] theme-transition">
                                                            <span className="text-[var(--text-secondary)] theme-transition">Product line</span>
                                                            <span className="font-medium text-[var(--text-main)] theme-transition">{paint.product_line}</span>
                                                        </div>
                                                    )}
                                                    {paint.base && (
                                                        <div className="flex justify-between py-1 border-b border-[var(--border)] theme-transition">
                                                            <span className="text-[var(--text-secondary)] theme-transition">Base</span>
                                                            <span className="font-medium text-[var(--text-main)] theme-transition">{paint.base}</span>
                                                        </div>
                                                    )}
                                                    {rp.date_painted && (
                                                        <div className="flex justify-between py-1 border-b border-[var(--border)] theme-transition">
                                                            <span className="text-[var(--text-secondary)] flex items-center theme-transition">
                                                                <Calendar size={12} className="mr-1" /> Painted
                                                            </span>
                                                            <span className="font-medium text-[var(--text-main)] theme-transition">
                                                                {new Date(rp.date_painted + 'T00:00:00').toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {rp.notes && (
                                                        <div className="py-1 theme-transition">
                                                            <span className="text-[var(--text-secondary)] text-xs theme-transition">Surface notes</span>
                                                            <p className="text-[var(--text-main)] mt-0.5 theme-transition">{rp.notes}</p>
                                                        </div>
                                                    )}
                                                    {paint.color_code && (
                                                        <div className="pt-2">
                                                            <button
                                                                onClick={() => copyHexCode(paint.color_code)}
                                                                className="w-full py-2 text-xs font-medium text-center border border-[var(--border)] rounded-lg hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition theme-transition flex items-center justify-center space-x-1"
                                                            >
                                                                <Copy size={14} />
                                                                <span>Copy color code</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </Card>
                                    );
                                })}

                                {/* Add Paint Card — opens the attach modal (paint picker first) */}
                                <button
                                    onClick={() => { setEditingRoomPaint(null); setShowRoomPaintModal(true); }}
                                    className="border-2 border-dashed border-[var(--border)] rounded-2xl p-5 flex flex-col items-center justify-center text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--primary)] hover:bg-[var(--primary-bg)] transition-all min-h-[280px] theme-transition"
                                >
                                    <Plus size={32} />
                                    <span className="mt-2 font-medium">Add Paint</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Floor Tab */}
                {activeTab === 'floor' && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <p className="text-[var(--text-secondary)] text-sm theme-transition">
                                Track flooring materials for easy replacement and refinishing
                            </p>
                        </div>

                        {floorLoading ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {floorDetails.map((detail) => (
                                    <Card key={detail.id} className="overflow-hidden">
                                        <div className="p-5">
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <h3 className="font-headline font-bold text-lg text-[var(--text-main)] theme-transition">
                                                        {detail.name}
                                                    </h3>
                                                    {detail.floor_type && (
                                                        <Badge variant="default" className="mt-1 capitalize">
                                                            {detail.floor_type.replace(/_/g, ' ')}
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => navigate(`/room/${id}/floor-details/${detail.id}/edit`, { state: { floorDetail: detail } })}
                                                        className="p-1.5 rounded-md hover:bg-[var(--bg-hover)] text-[var(--text-muted)] hover:text-[var(--primary)] transition theme-transition"
                                                        title="Edit"
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => setFloorToDelete(detail.id)}
                                                        className="p-1.5 rounded-md hover:bg-red-50 text-[var(--text-muted)] hover:text-red-500 transition theme-transition"
                                                        title="Delete"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="space-y-2 text-sm mt-3">
                                                {detail.brand && (
                                                    <div className="flex justify-between py-1 border-b border-[var(--border)] theme-transition">
                                                        <span className="text-[var(--text-secondary)] theme-transition">Brand</span>
                                                        <span className="font-medium text-[var(--text-main)] theme-transition">{detail.brand}</span>
                                                    </div>
                                                )}
                                                {detail.color_style && (
                                                    <div className="flex justify-between py-1 border-b border-[var(--border)] theme-transition">
                                                        <span className="text-[var(--text-secondary)] theme-transition">Color / Style</span>
                                                        <span className="font-medium text-[var(--text-main)] theme-transition">{detail.color_style}</span>
                                                    </div>
                                                )}
                                                {detail.install_date && (
                                                    <div className="flex justify-between py-1 border-b border-[var(--border)] theme-transition">
                                                        <span className="text-[var(--text-secondary)] flex items-center theme-transition">
                                                            <Calendar size={12} className="mr-1" /> Installed
                                                        </span>
                                                        <span className="font-medium text-[var(--text-main)] theme-transition">
                                                            {new Date(detail.install_date + 'T00:00:00').toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                )}
                                                {detail.purchase_date && (
                                                    <div className="flex justify-between py-1 theme-transition">
                                                        <span className="text-[var(--text-secondary)] flex items-center theme-transition">
                                                            <Calendar size={12} className="mr-1" /> Purchased
                                                        </span>
                                                        <span className="font-medium text-[var(--text-main)] theme-transition">
                                                            {new Date(detail.purchase_date + 'T00:00:00').toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </Card>
                                ))}

                                <button
                                    onClick={() => navigate(`/room/${id}/floor-details/new`)}
                                    className="border-2 border-dashed border-[var(--border)] rounded-2xl p-5 flex flex-col items-center justify-center text-[var(--text-muted)] hover:border-[var(--primary)] hover:text-[var(--primary)] hover:bg-[var(--primary-bg)] transition-all min-h-[220px] theme-transition"
                                >
                                    <Plus size={32} />
                                    <span className="mt-2 font-medium">Add Flooring</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Items Tab — numbered list */}
                {activeTab === 'items' && (
                    <div className="space-y-5">
                        {room.items && room.items.length > 0 ? (
                            <>
                                <div className="flex flex-col gap-4">
                                    {room.items.map((item, idx) => (
                                        <div
                                            key={item.id}
                                            data-testid="item-card"
                                            onClick={() => navigate(`/inventory/edit/${item.id}`)}
                                            className="group bg-[var(--bg-card)] p-5 rounded-2xl flex items-center justify-between hover:shadow-[0_8px_24px_rgba(0,0,0,0.04)] transition-all cursor-pointer"
                                        >
                                            <div className="flex items-center gap-5">
                                                <span className="text-[color-mix(in_srgb,var(--text-muted),transparent_60%)] font-headline font-black text-2xl italic w-8">
                                                    {String(idx + 1).padStart(2, '0')}
                                                </span>
                                                {item.images?.[0]?.url && (
                                                    <div className="w-12 h-12 rounded-lg overflow-hidden">
                                                        <img src={item.images[0].url} alt={item.name} className="w-full h-full object-cover" />
                                                    </div>
                                                )}
                                                <div>
                                                    <h4 className="font-bold text-[var(--text-main)]">{item.name}</h4>
                                                    <p className="text-xs text-[var(--text-secondary)]">
                                                        {item.category || 'Uncategorized'}
                                                        {item.quantity ? ` • Qty: ${item.quantity}` : ''}
                                                    </p>
                                                </div>
                                            </div>
                                            <ArrowRight size={16} className="text-[var(--text-muted)] group-hover:text-[var(--primary)] transition-colors" />
                                        </div>
                                    ))}
                                </div>
                                {itemsPagination.hasMore && (
                                    <div className="text-center">
                                        <button
                                            onClick={loadMoreItems}
                                            disabled={loadingMore.items}
                                            className="px-6 py-2.5 bg-[var(--bg-hover)] text-[var(--text-main)] rounded-xl hover:bg-[var(--bg-elevated)] transition font-medium text-sm disabled:opacity-50"
                                        >
                                            {loadingMore.items ? 'Loading...' : `Load More (${itemsPagination.total - room.items.length} remaining)`}
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-12">
                                <Package size={48} className="mx-auto text-[var(--text-muted)] mb-4" />
                                <h3 className="font-headline text-lg font-bold text-[var(--text-main)] mb-2">No items in this room</h3>
                                <p className="text-[var(--text-secondary)] mb-4">Start tracking items in this space</p>
                                <button
                                    onClick={() => navigate('/inventory/manual-entry')}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--primary)] text-white rounded-xl hover:opacity-90 transition font-medium text-sm"
                                >
                                    <Plus size={18} />
                                    <span>Add Item</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Tasks Tab */}
                {activeTab === 'tasks' && (
                    <div className="space-y-5">
                        <div className="flex justify-end">
                            <button
                                onClick={goToCreateTask}
                                className="flex items-center gap-2 px-4 py-2.5 bg-[var(--primary)] text-white rounded-xl font-medium text-sm hover:opacity-90 transition"
                            >
                                <Plus size={16} />
                                <span>New Task</span>
                            </button>
                        </div>
                        {room.tasks && room.tasks.length > 0 ? (
                            <>
                                <div className="flex flex-col gap-3">
                                    {room.tasks.map((task) => (
                                        <div key={task.id} className="bg-[var(--bg-card)] p-5 rounded-2xl shadow-[var(--shadow-sm)]">
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <h4 className="font-bold text-[var(--text-main)] mb-1">{task.title}</h4>
                                                    {task.description && (
                                                        <p className="text-sm text-[var(--text-secondary)] mb-3">{task.description}</p>
                                                    )}
                                                    <div className="flex flex-wrap gap-2">
                                                        {task.status && (
                                                            <Badge status={task.status === 'completed' ? 'Completed' : 'Pending'} />
                                                        )}
                                                        {task.priority && (
                                                            <Badge status={task.priority === 'high' ? 'High Priority' : task.priority === 'medium' ? 'Scheduled' : 'Routine'} />
                                                        )}
                                                    </div>
                                                </div>
                                                {task.due_date && (
                                                    <span className="text-xs text-[var(--text-muted)] whitespace-nowrap ml-4">
                                                        Due: {new Date(task.due_date).toLocaleDateString()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {tasksPagination.hasMore && (
                                    <div className="text-center">
                                        <button
                                            onClick={loadMoreTasks}
                                            disabled={loadingMore.tasks}
                                            className="px-6 py-2.5 bg-[var(--bg-hover)] text-[var(--text-main)] rounded-xl hover:bg-[var(--bg-elevated)] transition font-medium text-sm disabled:opacity-50"
                                        >
                                            {loadingMore.tasks ? 'Loading...' : `Load More (${tasksPagination.total - room.tasks.length} remaining)`}
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-12">
                                <CheckSquare size={48} className="mx-auto text-[var(--text-muted)] mb-4" />
                                <h3 className="font-headline text-lg font-bold text-[var(--text-main)] mb-2">No tasks for this room</h3>
                                <p className="text-[var(--text-secondary)] mb-4">Create a task to track maintenance or improvements</p>
                                <button
                                    onClick={goToCreateTask}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--primary)] text-white rounded-xl hover:opacity-90 transition font-medium text-sm"
                                >
                                    <Plus size={18} />
                                    <span>Create Task</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Shared paint editor — affects every room that references this paint */}
            <PaintFormModal
                isOpen={showPaintFormModal}
                onClose={() => { setShowPaintFormModal(false); setEditingSharedPaint(null); pendingAttachAfterCreate.current = false; }}
                onSave={handleSaveSharedPaint}
                paint={editingSharedPaint}
                saving={paintSaving}
            />

            {/* Room↔Paint junction editor — surface, date, notes per application */}
            <RoomPaintModal
                isOpen={showRoomPaintModal}
                onClose={() => { setShowRoomPaintModal(false); setEditingRoomPaint(null); }}
                onSave={handleSaveRoomPaint}
                roomPaint={editingRoomPaint}
                roomId={id}
                saving={roomPaintSaving}
                onCreatePaint={() => {
                    // Bridge from the attach flow into the create-paint flow. When the
                    // user finishes creating a new paint, the save handler reads this
                    // ref and re-opens the attach modal so they don't lose their place.
                    pendingAttachAfterCreate.current = true;
                    setShowRoomPaintModal(false);
                    setEditingSharedPaint(null);
                    setShowPaintFormModal(true);
                }}
            />

            {/* Delete Room Modal */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteRoom}
                isLoading={deletingRoom}
                title={`Delete ${room?.name}?`}
                message={
                    <>
                        Are you sure you want to delete <strong>{room?.name}</strong>? Any associated items and tasks will be unassigned but not deleted. This action cannot be undone.
                    </>
                }
                confirmText={deletingRoom ? 'Deleting...' : 'Delete'}
                isDanger={true}
                icon={Trash2}
            />

            {/* Detach from Room — only removes the junction. The Paint itself
                persists in the library so the user can re-attach it elsewhere.
                Deleting the underlying Paint happens on the Paint Library page. */}
            <ConfirmationModal
                isOpen={roomPaintToDetach !== null}
                onClose={() => setRoomPaintToDetach(null)}
                onConfirm={handleDetachRoomPaint}
                isLoading={detachingRoomPaint}
                title="Detach this paint?"
                message="The paint stays in your library — you can attach it to another room later. Only the connection to this room and surface will be removed."
                confirmText={detachingRoomPaint ? 'Detaching...' : 'Detach'}
                isDanger={true}
                icon={Trash2}
            />

            {/* Delete Floor Detail Modal */}
            <ConfirmationModal
                isOpen={floorToDelete !== null}
                onClose={() => setFloorToDelete(null)}
                onConfirm={handleDeleteFloorDetail}
                isLoading={deletingFloor}
                title="Delete floor detail?"
                message="Are you sure you want to delete this floor detail? This action cannot be undone."
                confirmText={deletingFloor ? 'Deleting...' : 'Delete'}
                isDanger={true}
                icon={Trash2}
            />

            {/* Delete User Floor Plan Modal. Only the user-created plan is
                deleted — the original room scan is a separate record and
                is never touched. */}
            <ConfirmationModal
                isOpen={planToDelete !== null}
                onClose={() => setPlanToDelete(null)}
                onConfirm={handleDeletePlan}
                isLoading={deletingPlan}
                title={`Delete ${planToDelete?.name || 'this floor plan'}?`}
                message="All walls, doors, and windows in this plan will be deleted. The original room scan is not affected. This action cannot be undone."
                confirmText={deletingPlan ? 'Deleting...' : 'Delete'}
                isDanger={true}
                icon={Trash2}
            />
        </div>
    );
};

export default RoomDetails;
